import './ExceptionFileTypes.scss';
import {Box, Typography} from '@mui/material';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import ArrRichTextEditor from '../../common/arr-rich-text-editor/ArrRichTextEditor';
import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import {useDispatch, useSelector} from 'react-redux';
import {useGetExceptionFileTypes} from '../../../hooks/documentPrinting/useDocumentPrinting';
import {
  setExceptionFileTypes,
  setSaveBatchPrint,
} from '../../../redux/slice/batchPrintSlice';
import ArrRadio from '../../common/arr-radio/ArrRadio';
import {RootState} from '../../../redux/store';
import {useEffect} from 'react';

const shortInfo = () => <p tabIndex={0}>{t('batchPrint.exceptionFileTypesStep.toolTip')}</p>;
const shortExcelInfo = () => (
  <>
    <p data-testid={`exception-file-types-excel-files-info1`} tabIndex={0}>
      {t('batchPrint.exceptionFileTypesStep.excelFilesInfo1')}
    </p>
    <p data-testid={`exception-file-types-excel-files-info2`} tabIndex={0}>
      {t('batchPrint.exceptionFileTypesStep.excelFilesInfo2')}
    </p>
  </>
);
const shortOtherFilesInfo = () => (
  <>
    <p data-testid={`exception-file-types-other-files-info1`} tabIndex={0}>
      {t('batchPrint.exceptionFileTypesStep.otherFilesInfo1')}
    </p>
    <p data-testid={`exception-file-types-other-files-info2`} tabIndex={0}>
      {t('batchPrint.exceptionFileTypesStep.otherFilesInfo2')}
    </p>
  </>
);

const ExceptionFileTypes = () => {
  const dispatch = useDispatch();
  const batchPrint = useSelector((state: RootState) => state.batchPrint);

  const sourceType = batchPrint?.selectedSourceActionType;
  const selectedSource = batchPrint?.selectedSource;

  const {
    issueDocuments,
    includeMediaFilePlaceHolder,
    mediaFilePlaceHolderHtml,
    isExcelFileSkipped,
    includeExcelFilePlaceHolder,
    excelFilePlaceHolderHtml,
    includeOtherExceptionFileTypesPlaceHolder,
    otherExceptionFileTypesPlaceHolderHtml,
  } = batchPrint?.saveBatchPrint?.batchPrintDTO || {};

  const sourceContextType =
    sourceType === 'search'
      ? 'SAVEDSEARCHES'
      : sourceType === 'tag'
        ? 'TAGS'
        : 'FOLDERS';

  const payload = {
    id: Number(selectedSource),
    sourceContextTypeCode: sourceContextType,
    issueDocuments,
  };

  const {data: getExceptionFileTypeData} = useGetExceptionFileTypes(payload);

  const exceptionData = getExceptionFileTypeData ?? {
    mediaFilesDocCount: 0,
    excelFilesDocCount: 0,
    otherExceptionFileTypes: 0,
    otherExceptionFileDocsWithPDFOrTiff: 0,
  };

  const zeroState =
    exceptionData.mediaFilesDocCount === 0 &&
    exceptionData.excelFilesDocCount === 0 &&
    exceptionData.otherExceptionFileTypes === 0;

  const {
    mediaFilesDocCount = 0,
    excelFilesDocCount = 0,
    otherExceptionFileTypes = 0,
  } = getExceptionFileTypeData ?? {};

  useEffect(() => {
    dispatch(
      setExceptionFileTypes({
        excelFilesDocCount,
        mediaFilesDocCount,
        otherExceptionFileTypes,
      })
    );
    dispatch(
      setSaveBatchPrint({
        includeMediaFilePlaceHolder:
          mediaFilesDocCount === 0 ? false : includeMediaFilePlaceHolder,
        isExcelFileSkipped:
          excelFilesDocCount === 0 ? false : isExcelFileSkipped,
        includeExcelFilePlaceHolder:
          excelFilesDocCount === 0 ? false : includeExcelFilePlaceHolder,
        includeOtherExceptionFileTypesPlaceHolder:
          otherExceptionFileTypes === 0
            ? false
            : includeOtherExceptionFileTypesPlaceHolder,
      })
    );
  }, [
    dispatch,
    excelFilesDocCount,
    mediaFilesDocCount,
    otherExceptionFileTypes,
  ]);

  return (
    <Box className="exception-file-types-container">
      <Box className="step-header">
        <Typography
          className="typo-text subhead2_size"
          data-testid={`exception-file-types-exception-file-types`}
          tabIndex={0}
        >
          {t('batchPrint.exceptionFileTypes')}
        </Typography>
        <ArrTooltipWrapper
          title={t('batchPrint.exceptionFileTypes')}
          closeTooltipOnEscPress={true}
          trigger={
            <InfoIcon
              aria-label="info-icon"
              className="info-icon"
              data-testid="exception-file-types-info"
              tabIndex={0}
            />
          }
        >
          {shortInfo()}
        </ArrTooltipWrapper>
      </Box>
      <Box className="step-content">
        <Box className="step-header-box">
          <Typography
            className="typo-text body2_size"
            data-testid={`exception-file-types-header`}
            tabIndex={0}
          >
            {t('batchPrint.exceptionFileTypesStep.header')}
          </Typography>
        </Box>

        {!zeroState && (
          <hr className="divider" data-testid={`exception-file-types-hr`} />
        )}

        <Box className="step-media-box">
          <Box className="menu-box">
            <Box className="menu-header-box">
              <Box className="menu-header">
                <Typography
                  className="typo-text subhead4_size"
                  data-testid={`exception-file-types-media-files`}
                  tabIndex={0}
                >
                  {t('batchPrint.exceptionFileTypesStep.mediaFiles')}
                </Typography>
                <ArrTooltipWrapper
                  title={t('batchPrint.exceptionFileTypesStep.mediaFiles')}
                  closeTooltipOnEscPress={true}
                  trigger={
                    <InfoIcon
                      className="info-icon"
                      data-testid="media-files-info"
                      tabIndex={0}
                      aria-label="info-icon"
                    />
                  }
                >
                  <p tabIndex={0}>{t('batchPrint.exceptionFileTypesStep.mediaFilesInfo')}</p>
                </ArrTooltipWrapper>
              </Box>

              <Typography
                className="typo-text body2_size body-text-color"
                data-testid={`exception-file-types-media-file-count`}
                tabIndex={0}
              >
                {exceptionData.mediaFilesDocCount === 0
                  ? t('batchPrint.exceptionFileTypesStep.noMediaFilesDesc')
                  : t(
                      'batchPrint.exceptionFileTypesStep.mediaFilesDesc'
                    ).replace(
                      '{{count}}',
                      String(exceptionData.mediaFilesDocCount)
                    )}
              </Typography>
            </Box>

            {exceptionData.mediaFilesDocCount !== 0 && (
              <Box className="menu-item" tabIndex={0}>
                <ArrToggle
                  checked={includeMediaFilePlaceHolder}
                  onChange={() =>
                    dispatch(
                      setSaveBatchPrint({
                        includeMediaFilePlaceHolder:
                          !includeMediaFilePlaceHolder,
                      })
                    )
                  }
                  dataTestId="menu-item-toggle"
                  slotProps={{
                    input: {
                      'aria-label': "menu-item-toggle",
                      'aria-checked': includeMediaFilePlaceHolder,
                      role: 'switch',
                    },
                  }}
                />
                <Typography
                  className="typo-text body2_size"
                  data-testid={`exception-file-types-enable-media-placeholder`}
                  tabIndex={0}
                >
                  {t(
                    'batchPrint.exceptionFileTypesStep.enableMediaPlaceholder'
                  )}
                </Typography>
              </Box>
            )}
          </Box>

          <Box className="editor-box" tabIndex={0}>
            {exceptionData.mediaFilesDocCount !== 0 &&
              includeMediaFilePlaceHolder && (
                <ArrRichTextEditor
                  dataTestId="media-file"
                  value={mediaFilePlaceHolderHtml}
                  onChange={(e) =>
                    dispatch(
                      setSaveBatchPrint({
                        mediaFilePlaceHolderHtml: e,
                      })
                    )
                  }
                />
              )}
          </Box>
        </Box>

        {!zeroState && (
          <hr className="divider" data-testid={`exception-file-types-hr-btn`} />
        )}

        <Box className="step-media-box">
          <Box className="menu-box">
            <Box className="menu-header-box">
              <Box className="menu-header">
                <Typography
                  className="typo-text subhead4_size"
                  data-testid={`exception-file-types-excel-files`}
                  tabIndex={0}
                >
                  {t('batchPrint.exceptionFileTypesStep.excelFiles')}
                </Typography>
                <ArrTooltipWrapper
                  title={t('batchPrint.exceptionFileTypesStep.excelFiles')}
                  closeTooltipOnEscPress={true}
                  trigger={
                    <InfoIcon
                      className="info-icon"
                      data-testid="excel-files-info"
                      tabIndex={0}
                    />
                  }
                >
                  {shortExcelInfo()}
                </ArrTooltipWrapper>
              </Box>

              <Typography
                className="typo-text body2_size body-text-color"
                data-testid={`exception-file-types-excel-file-count`}
                tabIndex={0}
              >
                {exceptionData.excelFilesDocCount === 0 ? (
                  t('batchPrint.exceptionFileTypesStep.noExcelFilesDesc')
                ) : (
                  <>
                    {t(
                      'batchPrint.exceptionFileTypesStep.excelFilesDesc'
                    ).replace(
                      '{{count}}',
                      String(exceptionData.excelFilesDocCount)
                    )}
                    <br data-testid={`exception-file-types-br`} />
                    {t('batchPrint.exceptionFileTypesStep.excelFilesDesc1')}
                  </>
                )}
              </Typography>
            </Box>

            {exceptionData.excelFilesDocCount !== 0 && (
              <Box className="menu-item">
                <ArrRadio
                  dataTestId="exception-excel-file-radio"
                  id="exception-excel-file-radio"
                  name="exception-excel-file-radio"
                  value={isExcelFileSkipped ? '0' : '1'}
                  onChange={(e) =>
                    dispatch(
                      setSaveBatchPrint({
                        isExcelFileSkipped: e === '0',
                        includeExcelFilePlaceHolder: e === '0' ? true : null,
                      })
                    )
                  }
                  data={[
                    {value: '1', label: 'Print'},
                    {value: '0', label: 'Skip'},
                  ]}
                  size="small"
                  
                 
                />
              </Box>
            )}

            {exceptionData.excelFilesDocCount !== 0 && isExcelFileSkipped && (
              <Box className="menu-item" tabIndex={0}>
                <ArrToggle
                  checked={includeExcelFilePlaceHolder}
                  onChange={() =>
                    dispatch(
                      setSaveBatchPrint({
                        includeExcelFilePlaceHolder:
                          !includeExcelFilePlaceHolder,
                      })
                    )
                  }
                  slotProps={{
                    input: {
                      'aria-label': "excel-skip-toggle",
                      'aria-checked': includeExcelFilePlaceHolder,
                      role: 'switch',
                    },
                  }}
                  dataTestId="excel-skip-toggle"
                />
                <Typography
                  className="typo-text body2_size"
                  data-testid={`exception-file-types-enable-excel-placeholder`}
                  tabIndex={0}
                >
                  {t(
                    'batchPrint.exceptionFileTypesStep.enableExcelPlaceholder'
                  )}
                </Typography>
              </Box>
            )}
          </Box>

          <Box className="editor-box" tabIndex={0}>
            {exceptionData.excelFilesDocCount !== 0 &&
              includeExcelFilePlaceHolder && (
                <ArrRichTextEditor
                  dataTestId="excel-file"
                  value={excelFilePlaceHolderHtml}
                  onChange={(e) =>
                    dispatch(
                      setSaveBatchPrint({
                        excelFilePlaceHolderHtml: e,
                      })
                    )
                  }
                />
              )}
          </Box>
        </Box>

        {!zeroState && (
          <hr
            className="divider"
            data-testid={`exception-file-types-hr-button`}
          />
        )}

        <Box className="step-media-box">
          <Box className="menu-box">
            <Box className="menu-header-box">
              <Box className="menu-header">
                <Typography
                  className="typo-text subhead4_size"
                  data-testid={`exception-file-types-other-files`}
                  tabIndex={0}
                >
                  {t('batchPrint.exceptionFileTypesStep.otherFiles')}
                </Typography>
                <ArrTooltipWrapper
                  title={t('batchPrint.exceptionFileTypesStep.otherFiles')}
                  aria-label={t('batchPrint.exceptionFileTypesStep.otherFiles')}
                  closeTooltipOnEscPress={true}
                  trigger={
                    <InfoIcon
                      className="info-icon"
                      data-testid="other-files-info"
                      tabIndex={0}
                      aria-label="info-icon"
                    />
                  } 
                >
                  {shortOtherFilesInfo()}
                </ArrTooltipWrapper>
              </Box>

              <Typography
                className="typo-text body2_size body-text-color"
                data-testid={`exception-file-types-other-exception-file-count`}
                tabIndex={0}
              >
                {exceptionData.otherExceptionFileTypes === 0
                  ? t('batchPrint.exceptionFileTypesStep.noOtherFilesDesc')
                  : t('batchPrint.exceptionFileTypesStep.otherFilesDesc')
                      .replace(
                        '{{count}}',
                        String(exceptionData.otherExceptionFileTypes)
                      )
                      .replace(
                        '{{printableCount}}',
                        String(
                          exceptionData.otherExceptionFileDocsWithPDFOrTiff
                        )
                      )
                      .replace(
                        '{{skippedCount}}',
                        String(
                          exceptionData.otherExceptionFileTypes -
                            exceptionData.otherExceptionFileDocsWithPDFOrTiff
                        )
                      )}
              </Typography>
            </Box>

            {exceptionData.otherExceptionFileTypes !== 0 && (
              <Box className="menu-item" tabIndex={0}>
                <ArrToggle
                  checked={includeOtherExceptionFileTypesPlaceHolder}
                  onChange={() =>
                    dispatch(
                      setSaveBatchPrint({
                        includeOtherExceptionFileTypesPlaceHolder:
                          !includeOtherExceptionFileTypesPlaceHolder,
                      })
                    )
                  }
                  dataTestId="exceptional-file-toggle"
                  slotProps={{
                    input: {
                      'aria-label': "exceptional-file-toggle",
                      'aria-checked':includeOtherExceptionFileTypesPlaceHolder,
                      role: 'switch',
                    },
                  }}
                />
                <Typography
                  className="typo-text body2_size"
                  data-testid={`exception-file-types-enable-other-placeholder`}
                  tabIndex={0}
                >
                  {t(
                    'batchPrint.exceptionFileTypesStep.enableOtherPlaceholder'
                  )}
                </Typography>
              </Box>
            )}
          </Box>

          <Box className="editor-box" tabIndex={0}>
            {exceptionData.otherExceptionFileTypes !== 0 &&
              includeOtherExceptionFileTypesPlaceHolder && (
                <ArrRichTextEditor
                  dataTestId="otherException-file"
                  value={otherExceptionFileTypesPlaceHolderHtml}
                  onChange={(e) =>
                    dispatch(
                      setSaveBatchPrint({
                        otherExceptionFileTypesPlaceHolderHtml: e,
                      })
                    )
                  }
                />
              )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ExceptionFileTypes;
